import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { Report, Section } from 'api/types'
import { useToast } from 'components/Toast'
import { Button, ErrorState, Spinner, TextAreaField } from 'components/ui'
import { SECTION_LABELS, WEEK_DAYS, WORKFLOW_LABELS, formatDate, parseIso } from 'lib/format'
import { t } from 'lib/i18n'
import { useAction, useAsync } from 'lib/hooks'

import { Icon, IconName } from './Icon'
import { ReturnReportDialog } from './ReturnReportDialog'

type Decision = 'approve' | 'return'

const REVIEW_SECTIONS: { section: Section | 'GENERAL'; icon: IconName }[] = [
  { icon: 'file', section: 'GENERAL' },
  { icon: 'users', section: 'STAFF' },
  { icon: 'list', section: 'LAB_TESTS' },
  { icon: 'box', section: 'MATERIALS' },
  { icon: 'truck', section: 'EQUIPMENT' },
  { icon: 'edit', section: 'WORKS' },
  { icon: 'mapPin', section: 'VISITS' },
]

const sectionLabel = (section: Section | 'GENERAL') =>
  section === 'GENERAL' ? t('معلومات عامة والطقس', 'General information and weather') : SECTION_LABELS[section]

const weekdayFromDate = (iso: string) => WEEK_DAYS[(parseIso(iso).getUTCDay() + 1) % 7]

const isSectionComplete = (report: Report, section: Section | 'GENERAL') => {
  switch (section) {
    case 'GENERAL':
      return Boolean(report.weather)
    case 'STAFF':
      return report.staff.length > 0
    case 'LAB_TESTS':
      return report.labTests.length > 0
    case 'MATERIALS':
      return report.materials.length > 0
    case 'EQUIPMENT':
      return report.equipment.length > 0
    case 'WORKS':
      return report.works.length > 0
    case 'VISITS':
      return report.visits.length > 0
    default:
      return false
  }
}

const initials = (name?: string | null) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || t('؟', '?')

const CommentComposer = ({ busy, onSubmit, onCancel }: { busy: boolean; onSubmit: (text: string) => void; onCancel: () => void }) => {
  const [text, setText] = useState('')
  return (
    <div className="review-panel__composer">
      <TextAreaField label={t('تعليق', 'Comment')} rows={2} value={text} onChange={(event) => setText(event.target.value)} />
      <div className="row-actions">
        <Button size="sm" onClick={onCancel}>
          {t('إلغاء', 'Cancel')}
        </Button>
        <Button size="sm" variant="primary" busy={busy} disabled={!text.trim()} onClick={() => onSubmit(text.trim())}>
          {t('إرسال', 'Send')}
        </Button>
      </div>
    </div>
  )
}

export const ReviewPanel = ({ reportId, onClose, onDecided }: { reportId: string; onClose: () => void; onDecided: () => void }) => {
  const notify = useToast()
  const { busy, run } = useAction()
  const { data: report, error, loading, reload } = useAsync(() => api.fetchReport(reportId), [reportId])
  const [decision, setDecision] = useState<Decision | null>(null)
  const [returning, setReturning] = useState(false)
  const [commentingOn, setCommentingOn] = useState<Section | null>(null)

  const commentCounts = useMemo(() => {
    const counts = new Map<Section, number>()
    report?.notes.forEach((note) => counts.set(note.section, (counts.get(note.section) ?? 0) + 1))
    return counts
  }, [report])

  const completeCount = report ? REVIEW_SECTIONS.filter((item) => isSectionComplete(report, item.section)).length : 0

  const addComment = async (section: Section, text: string) => {
    const result = await run(`comment-${section}`, () => api.addSupervisionNote(reportId, text, section), t('تم إضافة التعليق', 'Comment added'))
    if (result) {
      setCommentingOn(null)
      reload()
    }
  }

  const approve = async () => {
    const result = await run('approve', () => api.transitionReport('approveReport', reportId))
    if (result) {
      notify(
        t('تم اعتماد التقرير', 'Report approved'),
        'success',
        report?.preparedByName ? [t(`تم إعلام ${report.preparedByName}.`, `${report.preparedByName} has been notified.`)] : undefined
      )
      onDecided()
    }
  }

  const confirmReturn = async (note: string, sections: Section[]) => {
    const result = await run('return', () => api.notedTransition('returnReport', reportId, note))
    if (result) {
      await Promise.all(sections.map((section) => api.addSupervisionNote(reportId, note, section)))
      notify(t('أصبح التقرير للمراجعة', 'The report is for review'))
      setReturning(false)
      onDecided()
    }
  }

  const submitDecision = () => {
    if (decision === 'approve') approve()
    else if (decision === 'return') setReturning(true)
  }

  return (
    <div className="sheet-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="sheet sheet--end" role="dialog" aria-modal="true" aria-label={t('مراجعة التقرير', 'Review report')}>
        {loading && !report ? (
          <div className="sheet__loading">
            <Spinner />
          </div>
        ) : error || !report ? (
          <div className="sheet__loading">
            <ErrorState message={error || t('تعذر تحميل التقرير', 'Could not load the report')} onRetry={reload} />
          </div>
        ) : (
          <>
            <header className="sheet__header">
              <div className="sheet__title-row">
                <h2>{t('مراجعة التقرير', 'Review report')}</h2>
                <button type="button" className="icon-button" aria-label={t('إغلاق', 'Close')} onClick={onClose}>
                  <Icon name="close" />
                </button>
              </div>
              <div className="sheet__report-head">
                <p className="muted small">{report.project.name}</p>
                <div className="sheet__title-row">
                  <h3>
                    {weekdayFromDate(report.reportDate)} · {formatDate(report.reportDate)}
                  </h3>
                  <span className="chip">{WORKFLOW_LABELS[report.workflow]}</span>
                </div>
                <div className="sheet__meta-row">
                  <span className="sheet__code">{report.code}</span>
                  <span className="sheet__divider" />
                  <span className="sheet__engineer">
                    <span className="sheet__avatar">{initials(report.preparedByName)}</span>
                    {report.preparedByName || '—'}
                  </span>
                </div>
              </div>
            </header>

            <div className="sheet__body">
              <div className="sheet__sections-head">
                <p>{t('الأقسام', 'Sections')}</p>
                <span className="muted small">
                  {t(`${completeCount} من ${REVIEW_SECTIONS.length} مكتمل`, `${completeCount} of ${REVIEW_SECTIONS.length} complete`)}
                </span>
              </div>
              <ul className="review-panel__list">
                {REVIEW_SECTIONS.map(({ section, icon }) => {
                  const complete = isSectionComplete(report, section)
                  const count = section === 'GENERAL' ? 0 : commentCounts.get(section) ?? 0
                  return (
                    <li key={section}>
                      <div className="review-panel__row">
                        <Icon name={icon} size={16} />
                        <span className="review-panel__label">{sectionLabel(section)}</span>
                        <span className={complete ? 'badge badge--complete' : 'badge badge--new'}>
                          {complete ? t('مكتمل', 'Complete') : t('فارغ', 'Empty')}
                        </span>
                        {count ? <span className="chip chip--info">{count}</span> : null}
                        {section !== 'GENERAL' ? (
                          <Button size="sm" variant="ghost" icon="send" onClick={() => setCommentingOn(section)}>
                            {t('تعليق', 'Comment')}
                          </Button>
                        ) : null}
                      </div>
                      {commentingOn === section ? (
                        <CommentComposer
                          busy={busy === `comment-${section}`}
                          onCancel={() => setCommentingOn(null)}
                          onSubmit={(text) => addComment(section, text)}
                        />
                      ) : null}
                    </li>
                  )
                })}
              </ul>

              {report.permissions.canApprove ? (
                <div className="review-panel__decision">
                  <p className="field__label">
                    {t('القرار', 'Decision')} <span className="field__required">*</span>
                  </p>
                  <button
                    type="button"
                    className={decision === 'approve' ? 'radio-card is-checked' : 'radio-card'}
                    onClick={() => setDecision('approve')}
                  >
                    <span className="radio-card__radio" />
                    <span className="radio-card__icon">
                      <Icon name="check" size={16} />
                    </span>
                    <span className="radio-card__text">
                      <strong>{t('اعتماد', 'Approve')}</strong>
                      <span className="muted small">{t('التقرير مكتمل وصحيح. سيُقفل بعد الاعتماد.', 'The report is complete and correct. It will be locked after approval.')}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={decision === 'return' ? 'radio-card is-checked' : 'radio-card'}
                    onClick={() => setDecision('return')}
                  >
                    <span className="radio-card__radio" />
                    <span className="radio-card__icon">
                      <Icon name="undo" size={16} />
                    </span>
                    <span className="radio-card__text">
                      <strong>{t('إرسال للمراجعة', 'Send for review')}</strong>
                      <span className="muted small">{t('يُرسل للمراجعة مع السبب.', 'It is sent for review with the reason.')}</span>
                    </span>
                  </button>
                </div>
              ) : (
                <Link to={`/projects/${report.projectId}/reports/${report.id}`} className="btn btn--block">
                  {t('فتح التقرير كاملاً', 'Open the full report')}
                </Link>
              )}
            </div>

            {report.permissions.canApprove ? (
              <footer className="sheet__footer">
                <p className="muted small">
                  {report.preparedByName
                    ? t(`سيتم إعلام ${report.preparedByName} بالبريد الإلكتروني.`, `${report.preparedByName} will be notified by email.`)
                    : ''}
                </p>
                <Button onClick={onClose}>{t('إلغاء', 'Cancel')}</Button>
                <Button variant="primary" busy={busy === 'approve'} disabled={!decision} onClick={submitDecision}>
                  {t('إرسال القرار', 'Submit decision')}
                </Button>
              </footer>
            ) : null}
          </>
        )}
      </aside>

      {returning && report ? (
        <ReturnReportDialog
          reportCode={report.code}
          engineerName={report.preparedByName}
          busy={busy === 'return'}
          onConfirm={confirmReturn}
          onClose={() => setReturning(false)}
        />
      ) : null}
    </div>
  )
}
